import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { SnackbarProvider } from 'notistack';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import theme from './theme';
import Layout from './components/Layout';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import CompanyClients from './pages/CompanyClients';
import CompanyList from './pages/CompanyList';
import Employees from './pages/Employees';
import Subscriptions from './pages/Subscriptions';
import ClientVisits from './pages/ClientVisits';
import Profile from './pages/Profile';
import Plans from './pages/Plans';
import EmployeeHistory from './pages/EmployeeHistory';
import AddWorker from './pages/AddWorker';
import WorkerPayData from './pages/WorkerPayData';
import QuickBooksInvoiceData from './pages/QuickBooksInvoiceData';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, profileStatus } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // If profile is incomplete and trying to access any route other than profile
  if (profileStatus === 'incomplete' && location.pathname !== '/profile') {
    return <Navigate to="/profile" replace />;
  }

  return children;
};

// Get Stripe key based on NODE_ENV
const getStripeKey = () => {
  // If explicitly set in env, use that
  if (process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
    return process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
  }
  
  // Otherwise, use environment-based keys
  const isProduction = process.env.NODE_ENV === 'production';
  
  if (isProduction) {
    // Production: Use live key
    return process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY_LIVE || 'pk_live_nGavRsxDhQKiCW0cIpYdcHWP00m7qLj1e6';
  } else {
    // Development: Use test key
    return process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY_TEST || 'pk_test_XXXXXXXXXXXXXXXXXXXXXXXX';
  }
};

const stripePromise = loadStripe(getStripeKey());

const App = () => {
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @media (min-width: 767px) {
        .css-k008qs {
          display: block !important;
          /* margin-left: 240px; */
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SnackbarProvider maxSnack={3}>
        <AuthProvider>
          <Elements stripe={stripePromise}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route
                path="/"
                element={
                  <PrivateRoute>
                    <Layout />
                  </PrivateRoute>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="company-clients" element={<CompanyClients />} />
                <Route path="companies" element={<CompanyList />} />
                <Route path="employees" element={<Employees />} />
                <Route path="add-worker" element={<AddWorker />} />
                <Route path="employee-history/:id" element={<EmployeeHistory />} />
                <Route path="worker-pay-data" element={<WorkerPayData />} />
                <Route path="quickbooks-invoice-data" element={<QuickBooksInvoiceData />} />
                <Route path="subscriptions" element={<Subscriptions />} />
                <Route path="client-visits" element={<ClientVisits />} />
                <Route path="profile" element={<Profile />} />
                <Route path="plans" element={<Plans />} />
              </Route>
            </Routes>
          </Elements>
        </AuthProvider>
      </SnackbarProvider>
    </ThemeProvider>
  );
};

export default App; 